import { Paper, Typography, Stack, Box, Divider, Button, Alert, alpha } from "@mui/material";
import { CalendarMonth, LocationOn, Lock } from "@mui/icons-material";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { ILocationList } from "../../../interfaces/ILocationList";
import { EventRegistrationStatus } from "../../../enums/EventRegistrationStatus";
import { IPublicEventDetails } from "../../../interfaces/IPublicEventDetails";
import EventShareButton from "./EventShareButton";
import { useApiClient } from "../../../ApiClientProvider";
import { ParticipantEventService } from "../../../services/participantEventService";
import { IUserRegistration } from "../../../interfaces/IUserRegistration";
import { RegistrationType } from "../../../enums/RegistrationType";
import { RegistrationStatus as ParticipantRegistrationStatus } from "../../../enums/RegistrationStatus";
import { resolveEventAvailability } from "../../../utils/eventAvailability";
import { formatEventTimeRange } from "../../../utils/eventTimeZones";

interface EventSidebarProps {
    event: IPublicEventDetails;
    locations: ILocationList[];
    isLoggedIn: boolean;
    registrationOpen: boolean;
    daysRemaining: number;
    handleRegister: () => void;
    handleReviewTickets?: () => void;
    handleAssignTickets?: () => void;
    handleBuyMoreTickets?: () => void;
}

function formatDateRange(startDate: string, endDate: string, isSingleDay?: boolean): string {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    // Trust the backend-computed flag rather than comparing calendar dates, which can
    // disagree with reality once a UTC timestamp is converted to local display time.
    if (isSingleDay) {
        return start.format("MMM D, YYYY");
    }
    if (start.year() !== end.year()) {
        return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
    } else if (start.month() !== end.month()) {
        return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
    } else if (start.date() !== end.date()) {
        return `${start.format("MMM D")}-${end.format("D, YYYY")}`;
    } else {
        return start.format("MMM D, YYYY");
    }
}

function formatDate(dateString: string): string {
    return dayjs(dateString).format("MMM D, YYYY");
}

export default function EventSidebar({
    event,
    locations,
    isLoggedIn,
    registrationOpen,
    daysRemaining,
    handleRegister,
    handleReviewTickets,
    handleAssignTickets,
    handleBuyMoreTickets,
}: EventSidebarProps) {
    const apiClient = useApiClient('applicationClient');
    const participantEventService = useMemo(() => new ParticipantEventService(apiClient), [apiClient]);
    const [registrationSummary, setRegistrationSummary] = useState<IUserRegistration | null>(null);

    useEffect(() => {
        const loadRegistrationSummary = async () => {
            if (!event?.id) {
                setRegistrationSummary(null);
                return;
            }

            // Only needed for participant-like states where wording differs from attendee registration.
            if (
                event.registrationStatus !== EventRegistrationStatus.Registered &&
                event.registrationStatus !== EventRegistrationStatus.Pending &&
                event.registrationStatus !== EventRegistrationStatus.RegistrationIncomplete
            ) {
                setRegistrationSummary(null);
                return;
            }

            try {
                const summary = await participantEventService.getUserRegistration(event.id);
                setRegistrationSummary(summary ?? null);
            } catch {
                // Sidebar is shared; ignore failures for users without participant context.
                setRegistrationSummary(null);
            }
        };

        void loadRegistrationSummary();
    }, [event?.id, event.registrationStatus, participantEventService]);

    const getAllLocations = (): ILocationList[] => {
        const locationIds = new Set<string>();
        const allLocations: ILocationList[] = [];
        event?.eventDays.forEach((day) => {
            const location = locations.find((loc) => loc.id === day.locationId);
            if (location && !locationIds.has(location.id)) {
                locationIds.add(location.id);
                allLocations.push(location);
            }
        });
        return allLocations;
    };

    const allLocations = getAllLocations();
    const canUserRegister = !event.isInviteOnly || event.registrationStatus == EventRegistrationStatus.Invited ||
        event.registrationStatus == EventRegistrationStatus.Registered || event.registrationStatus == EventRegistrationStatus.TicketsAvailable;

    // Alerts and actions based on registration status
    let statusAlert: React.ReactNode = null;
    let actionButton: React.ReactNode = null;

    const eventTimeRange = formatEventTimeRange(event.startUtc, event.endUtc, event.timeZoneId, event.eventDays);
    const availability = resolveEventAvailability({
        ticketAvailability: event.ticketAvailability,
        registrationDeadline: event.registrationDeadline.toString(),
        endDate: event.endDate.toString(),
        endUtc: event.endUtc,
    });
    // Waitlist entry runs through the same registration flow, so a sold-out event still offers it.
    const canStartRegistration = availability.kind === 'unknown' || availability.canBuy || availability.canJoinWaitlist;
    const registerLabel = availability.canJoinWaitlist ? "Join waitlist" : "Register Now";
    const availabilityAlert = !canStartRegistration && availability.kind !== 'registrationClosed' ? (
        <Alert severity={availability.tone === 'neutral' ? 'info' : availability.tone} sx={{ mb: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{availability.label}</Typography>
            <Typography variant="body2">{availability.description}</Typography>
        </Alert>
    ) : availability.canJoinWaitlist ? (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{availability.label}</Typography>
            <Typography variant="body2">You can join the waitlist and we'll tell you if tickets become available.</Typography>
        </Alert>
    ) : null;

    switch (event.registrationStatus) {
        case EventRegistrationStatus.Registered:
            if (registrationSummary?.type === RegistrationType.SessionDelegate || registrationSummary?.status === ParticipantRegistrationStatus.PendingTicketAllocation) {
                statusAlert = (
                    <Alert
                        severity="info"
                        sx={{
                            mb: 2,
                            borderRadius: 2,
                            border: `1px solid ${alpha('#0288d1', 0.3)}`,
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            You're participating in this event.
                        </Typography>
                        <Typography variant="body2">
                            You are assigned as a session delegate. Ticket allocation may still be pending.
                        </Typography>
                    </Alert>
                );
                break;
            }

            statusAlert = (
                <Alert
                    severity="success"
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        border: `1px solid ${alpha('#2e7d32', 0.3)}`,
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        You are registered for this event.
                    </Typography>
                </Alert>
            );
            break;
        case EventRegistrationStatus.PendingApproval:
            statusAlert = (
                <Alert
                    severity="warning"
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        border: `1px solid ${alpha('#ed6c02', 0.3)}`,
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Pending Approval
                    </Typography>
                    <Typography variant="body2">
                        Your registration is awaiting organiser approval. You'll be notified once reviewed.
                    </Typography>
                </Alert>
            );
            break;
        case EventRegistrationStatus.Waitlist:
            statusAlert = (
                <Alert
                    severity="info"
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        border: `1px solid ${alpha('#0288d1', 0.3)}`,
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        You're on the Waitlist
                    </Typography>
                    <Typography variant="body2">
                        You'll be notified when tickets become available.
                    </Typography>
                </Alert>
            );
            break;
        case EventRegistrationStatus.Pending:
            if (event.isPaymentPendingRegistration) {
                statusAlert = (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="subtitle2">Payment confirmation pending</Typography>
                        <Typography variant="body2">
                            Your QR codes will be available once your payment is confirmed. If you have just paid, check back shortly.
                        </Typography>
                    </Alert>
                );
                break;
            }
            statusAlert = (
                <Alert
                    severity="warning"
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        border: `1px solid ${alpha('#ed6c02', 0.3)}`,
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Tickets Pending
                    </Typography>
                    <Typography variant="body2">
                        You have tickets awaiting acceptance. Please review your tickets.
                    </Typography>
                </Alert>
            );
            actionButton = (
                <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleReviewTickets}
                    sx={{
                        borderRadius: '50px',
                        bgcolor: '#ed6c02',
                        color: 'white',
                        fontWeight: 600,
                        textTransform: "none",
                        px: 3,
                        py: 1.5,
                        boxShadow: '0 4px 14px rgba(237, 108, 2, 0.4)',
                        transition: "all 0.3s ease",
                        "&:hover": {
                            bgcolor: '#e65100',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(237, 108, 2, 0.5)',
                        },
                    }}
                >
                    Review Tickets
                </Button>
            );
            break;
        case EventRegistrationStatus.RegistrationIncomplete:
            statusAlert = (
                <Alert
                    severity="warning"
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        border: `1px solid ${alpha('#ed6c02', 0.3)}`,
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Complete Registration Required
                    </Typography>
                    <Typography variant="body2">
                        Your ticket has been accepted, but your registration form is incomplete. Complete your details to finish registration.
                    </Typography>
                </Alert>
            );
            actionButton = registrationOpen ? (
                <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleRegister}
                    sx={{
                        borderRadius: '50px',
                        bgcolor: '#ed6c02',
                        color: 'white',
                        fontWeight: 600,
                        textTransform: "none",
                        px: 3,
                        py: 1.5,
                        boxShadow: '0 4px 14px rgba(237, 108, 2, 0.4)',
                        transition: "all 0.3s ease",
                        "&:hover": {
                            bgcolor: '#e65100',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(237, 108, 2, 0.5)',
                        },
                    }}
                >
                    Complete Registration
                </Button>
            ) : null;
            break;
        case EventRegistrationStatus.TicketsAvailable:
            if (event.isWaitlistRegistration) {
                statusAlert = (
                    <Alert
                        severity="success"
                        sx={{
                            mb: 2,
                            borderRadius: 2,
                            border: `1px solid ${alpha('#2e7d32', 0.3)}`,
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Tickets Are Now Available
                        </Typography>
                        <Typography variant="body2">
                            You're on the waitlist and tickets are now available. Register now to secure your place.
                        </Typography>
                    </Alert>
                );

                if (registrationOpen) {
                    actionButton = (
                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            onClick={handleRegister}
                            sx={{
                                borderRadius: '50px',
                                bgcolor: '#493193',
                                color: 'white',
                                fontWeight: 600,
                                textTransform: "none",
                                px: 3,
                                py: 1.5,
                                boxShadow: '0 4px 14px rgba(73, 49, 147, 0.4)',
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    bgcolor: '#3a2676',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 20px rgba(73, 49, 147, 0.5)',
                                },
                            }}
                        >
                            Register Now
                        </Button>
                    );
                }
                break;
            }

            statusAlert = (
                <Alert
                    severity="info"
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        border: `1px solid ${alpha('#0288d1', 0.3)}`,
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Tickets Available
                    </Typography>
                    <Typography variant="body2">
                        You have tickets available to assign. Assign them to yourself or others to complete registration.
                    </Typography>
                </Alert>
            );
            actionButton = (
                <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleAssignTickets}
                    sx={{
                        borderRadius: '50px',
                        bgcolor: '#493193',
                        color: 'white',
                        fontWeight: 600,
                        textTransform: "none",
                        px: 3,
                        py: 1.5,
                        boxShadow: '0 4px 14px rgba(73, 49, 147, 0.4)',
                        transition: "all 0.3s ease",
                        "&:hover": {
                            bgcolor: '#3a2676',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(73, 49, 147, 0.5)',
                        },
                    }}
                >
                    Assign Tickets
                </Button>
            );
            break;
        case EventRegistrationStatus.Invited:
            statusAlert = (
                <Alert
                    severity={registrationOpen ? "success" : "info"}
                    sx={{
                        mb: 2,
                        borderRadius: 2,
                        border: registrationOpen
                            ? `1px solid ${alpha('#2e7d32', 0.3)}`
                            : `1px solid ${alpha('#0288d1', 0.3)}`,
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {registrationOpen ? "You've been invited!" : "You were invited"}
                    </Typography>
                    <Typography variant="body2">
                        {registrationOpen
                            ? "You have a valid invitation for this event. Register below to claim your spot."
                            : "You have a valid invitation for this event, but registration is now closed. Contact the organiser if you need help."}
                    </Typography>
                </Alert>
            );
            if (registrationOpen && canStartRegistration) {
                actionButton = (
                    <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        onClick={handleRegister}
                        sx={{
                            borderRadius: '50px',
                            bgcolor: '#493193',
                            color: 'white',
                            fontWeight: 600,
                            textTransform: "none",
                            px: 3,
                            py: 1.5,
                            boxShadow: '0 4px 14px rgba(73, 49, 147, 0.4)',
                            transition: "all 0.3s ease",
                            "&:hover": {
                                bgcolor: '#3a2676',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(73, 49, 147, 0.5)',
                            },
                        }}
                    >
                        {registerLabel}
                    </Button>
                );
            }
            break;
        case EventRegistrationStatus.NotRegistered:
            if (event.isInviteOnly) {
                statusAlert = (
                    <Alert
                        severity="warning"
                        icon={<Lock fontSize="small" />}
                        sx={{
                            mb: 2,
                            borderRadius: 2,
                            border: `1px solid ${alpha('#ed6c02', 0.3)}`,
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Invitation Required
                        </Typography>
                        <Typography variant="body2">
                            {isLoggedIn
                                ? "You don't currently have an invitation for this event. Contact the organiser if you'd like to attend."
                                : "This event is invitation only. Sign in with your invited account to check your invitation status or request access from the organiser."}
                        </Typography>
                    </Alert>
                );
            } else if (registrationOpen && canStartRegistration) {
                actionButton = (
                    <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        onClick={handleRegister}
                        sx={{
                            borderRadius: '50px',
                            bgcolor: '#493193',
                            color: 'white',
                            fontWeight: 600,
                            textTransform: "none",
                            px: 3,
                            py: 1.5,
                            boxShadow: '0 4px 14px rgba(73, 49, 147, 0.4)',
                            transition: "all 0.3s ease",
                            "&:hover": {
                                bgcolor: '#3a2676',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(73, 49, 147, 0.5)',
                            },
                        }}
                    >
                        {registerLabel}
                    </Button>
                );
            }
            break;
    }

    // Optionally, always show "Buy More Tickets" button
    const buyMoreButton = handleBuyMoreTickets &&
        !event.isPaymentPendingRegistration &&
        event.registrationStatus !== EventRegistrationStatus.NotRegistered &&
        event.registrationStatus !== EventRegistrationStatus.PendingApproval &&
        event.registrationStatus !== EventRegistrationStatus.Waitlist &&
        event.registrationStatus !== EventRegistrationStatus.Invited &&
        !(event.registrationStatus === EventRegistrationStatus.TicketsAvailable && event.isWaitlistRegistration) && (
            <Button
                variant="outlined"
                fullWidth
                size="large"
                onClick={handleBuyMoreTickets}
                sx={{
                    borderRadius: '50px',
                    borderColor: alpha('#493193', 0.3),
                    color: '#493193',
                    fontWeight: 600,
                    textTransform: "none",
                    px: 3,
                    py: 1.5,
                    mt: 1,
                    transition: "all 0.3s ease",
                    "&:hover": {
                        borderColor: '#493193',
                        bgcolor: alpha('#493193', 0.05),
                        transform: 'translateY(-2px)',
                    },
                }}
            >
                Buy More Tickets
            </Button>
        );

    return (
        <Box sx={{ position: { md: "sticky" }, top: { md: 90 }, maxHeight: { md: 'calc(100dvh - 90px)' }, overflowY: { md: 'auto' }, mb: { xs: 3, md: 0 } }}>
            <Paper
                elevation={3}
                sx={{
                    p: { xs: 2, md: 3 },
                    borderRadius: 3,
                    border: `1px solid ${alpha('#493193', 0.1)}`,
                    boxShadow: `0 4px 20px ${alpha('#493193', 0.08)}`,
                    mb: 3,
                }}
            >
                <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: "1.1rem", md: "1.25rem" },
                        background: `linear-gradient(135deg, #493193 0%, #2fadd8 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 2,
                    }}
                >
                    Event Details
                </Typography>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Date & Time
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                            <CalendarMonth fontSize="small" sx={{ mr: 1, color: '#493193' }} />
                            <Typography variant="body2">
                                {formatDateRange(event.startDate.toString(), event.endDate.toString(), event.isSingleDay)}
                            </Typography>
                        </Box>
                        {eventTimeRange && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 3.5 }}>
                                {eventTimeRange}
                            </Typography>
                        )}
                    </Box>
                    <Divider />
                    {allLocations.length > 0 && (
                        <>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Location
                                </Typography>
                                {allLocations.map((location) => (
                                    <Box
                                        key={location.id}
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            mt: 0.5,
                                        }}
                                    >
                                        <LocationOn fontSize="small" sx={{ mr: 1, color: '#493193', mt: 0.3 }} />
                                        <Box>
                                            <Typography variant="body2">{location.name}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {location.streetAddress}, {location.city}, {location.region}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                            <Divider />
                        </>
                    )}
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Registration Deadline
                        </Typography>
                        <Typography variant="body2">
                            {formatDate(event.registrationDeadline.toString())}
                        </Typography>
                        {registrationOpen ? (
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mt: 0.5,
                                    mb: 1,
                                    color: '#493193',
                                }}
                            >
                                {daysRemaining} {daysRemaining === 1 ? "day" : "days"} left to register
                            </Typography>

                        ) : (
                            <Typography variant="body2" color="error.main" sx={{ fontWeight: 500, mt: 0.5 }}>
                                Registration is closed
                            </Typography>
                        )}
                    </Box>
                </Stack>
                {statusAlert}
                {(event.registrationStatus === EventRegistrationStatus.NotRegistered || event.registrationStatus === EventRegistrationStatus.Invited)
                    && registrationOpen && !(event.isInviteOnly && event.registrationStatus === EventRegistrationStatus.NotRegistered)
                    && availabilityAlert}
                {actionButton}
                {buyMoreButton}

                {/* Share Event Button */}
                <Box sx={{ mt: 2 }}>
                    <EventShareButton
                        eventId={event.id || ''}
                        communityShareMode="participant"
                        showCommunityShare={isLoggedIn}
                        eventTitle={event.title}
                        eventStatus={event.status}
                        isInviteOnly={event.isInviteOnly}
                        isEventPublic={event.isEventPublic}
                        variant="outlined"
                        size="large"
                        disabled={false}
                    />
                </Box>
            </Paper>

        </Box>
    );
}